import { useQuery } from '@apollo/client'
import { GET_SPEC_INTELLIGENCE_RUN_STATUS } from 'api/graphQL/queries/specIntelligence.queries'
import type {
    GetSpecIntelligenceRunStatusInput,
    GetSpecIntelligenceRunStatusResponse,
    SpecIntelligenceRunStatus,
} from 'api/graphQL/types/specIntelligence.types'
import { useCallback, useEffect, useState } from 'react'

const POLL_INTERVAL_MS = 3000

/** Returned by {@link useSpecIntelligencePolling}. */
export interface UseSpecIntelligencePollingResult {
    /** True while a run is in progress (instance ID is set and not yet resolved). */
    isBuilding: boolean
    /** The backend process instance ID currently being tracked, or null. */
    pollingInstanceId: string | null
    /** Latest status from the polling query, or null when idle. */
    runStatus: SpecIntelligenceRunStatus | null
    /** Begin polling for the given instance ID. */
    startPolling: (instanceId: string) => void
}

/**
 * Manages the GET_SPEC_INTELLIGENCE_RUN_STATUS polling lifecycle at a level above
 * the drawer so the animation and status survive drawer close/tab switches.
 *
 * @param skip - When true, the polling query is suppressed regardless of instance ID.
 */
const useSpecIntelligencePolling = (skip = false): UseSpecIntelligencePollingResult => {
    const [pollingInstanceId, setPollingInstanceId] = useState<string | null>(null)
    const [runStatus, setRunStatus] = useState<SpecIntelligenceRunStatus | null>(null)

    const { data, loading, stopPolling } = useQuery<
        GetSpecIntelligenceRunStatusResponse,
        GetSpecIntelligenceRunStatusInput
    >(GET_SPEC_INTELLIGENCE_RUN_STATUS, {
        variables: { input: { instanceId: pollingInstanceId ?? '' } },
        pollInterval: skip || !pollingInstanceId ? 0 : POLL_INTERVAL_MS,
        fetchPolicy: 'network-only',
        skip: skip || !pollingInstanceId,
    })

    useEffect(() => {
        if (loading || !data) return
        const status = data.getSpecIntelligenceRunStatus
        setRunStatus(status)
        if (status && (status.state === 'Complete' || status.state === 'Error')) {
            stopPolling()
            setPollingInstanceId(null)
        }
    }, [data, loading, stopPolling])

    const startPolling = useCallback((instanceId: string) => {
        setRunStatus(null)
        setPollingInstanceId(instanceId)
    }, [])

    return {
        isBuilding: pollingInstanceId !== null,
        pollingInstanceId,
        runStatus,
        startPolling,
    }
}

export default useSpecIntelligencePolling
